import React, { useContext, useState, useEffect } from 'react';
import styled from "styled-components";
import * as MdIcons from "react-icons/md";
import { MdOutlineKeyboardAlt, MdInfo } from "react-icons/md"
import { BsDisplay} from "react-icons/bs";

import { VisualizationContext } from '../Visualization';
import { analysesUpdates, useAnalysesProvider } from '../../../Providers/AnalysesProvider';
import { useModalProvider } from '../../../Providers/ModalProvider';
import { Deployment, useEnvironmentVariablesProvider } from '../../../Providers/EnvironmentVariablesProvider';
import { useEndpointProvider } from '../../../Providers/EndpointProvider';
import { useBackendLinksProvider } from '../../../Providers/BackendLinksProvider';
import { useWorkspacesProvider } from '../../../Providers/WorkspacesProvider';
import ConfirmationModal from '../../../Components/ConfirmationModal/ConfirmationModal';
import { HotkeysModal } from './Modals/HotkeysModal';
import ZoomResolutionChecker from '../../../Components/ZoomResolutionChecker';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { layoutGroupsAtom, selectedLayoutGroupAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/Layout';
import { useOnMount } from '../../../Hooks/useOnMount';
import { currentlyActiveVisualizationAreaAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/Visualizations';
import { MobergButton, MobergButtonShape, MobergButtonVariant } from '../../../Components/MobergButton/MobergButton';
import { MobergTheme } from '../../../Components/MobergThemes/MobergColors';
import { MobergIconSize } from '../../../Components/MobergIcon/MobergIcon';

import { MobergFontFamily } from '../../../Components/MobergFont/MobergFont';
import { useDisplayOptimality } from '../../../Hooks/useDisplayOptimality';
import { useMobergTheme } from '../../../Providers/MobergThemeProvider';
import logoIcon from '../../../assets/images/icon.png';
import darkLogoIcon from '../../../assets/images/dark-icon.png';

import { MCPDeviceStatus } from "../../../Pages/Data/Visualize/DataReview/Components/React/MCPDeviceStatus"

import { AlertsFeed } from "../../../Pages/Data/Visualize/DataReview/Components/React/AlertsFeed"
import { MobergRow } from '../../../Moberg';
import { MRNError } from '../../../Pages/Data/Visualize/MRNError';
import { currentPatientFileInfoAtom } from '../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile';

/**
 * @name ToolBar
 * @category Visualization
 * @subcategory Components
 * 
 * @description The toolbar
 * 
 * @component
 * @param {*} props 
 */
function ToolBar() {
	const analysesProvider = useAnalysesProvider()
	const { createModal } = useModalProvider()
	const setActiveVisualizationArea = useSetRecoilState(currentlyActiveVisualizationAreaAtom)
	const [, setSelectedLayoutGroup] = useRecoilState(selectedLayoutGroupAtom)
	const { mrnError } = useRecoilValue(currentPatientFileInfoAtom)
	const layoutGroups = useRecoilValue(layoutGroupsAtom)
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const workspacesProvider = useWorkspacesProvider()

	const { deployment } = useEnvironmentVariablesProvider()

	const isClinical = deployment === Deployment.CLINICAL

	// Display settings state
	const [showZoomChecker, setShowZoomChecker] = useState(false)
	const [hasHeaderOverflow, setHasHeaderOverflow] = useState(false)
	const [patientName, setPatientName] = useState("Unknown")
	
	// Use custom hooks for browser detection and display optimality
	const { isOptimal: isDisplayOptimal } = useDisplayOptimality()
	const { colorScheme } = useMobergTheme()

	useOnMount(() => {
		analysesProvider.update(analysesUpdates.ANCILLARY_FILTER)
		setSelectedLayoutGroup(layoutGroups[0])

		try {
			setActiveVisualizationArea(layoutGroups[0].layouts[0].areas[0].area)
		} catch { }
	})



	const { dataObjectName, closeVisualization, updateHotkeyConfig, patient_id, siteName, dataObjectID, patientID } = useContext(VisualizationContext)

	// Fetch patient name for clinical deployment
	useEffect(() => {
		if (!isClinical || !patientID) {
			return
		}

		const fetchPatientData = async () => {
			try {
				const body = {
					workspace_name: workspacesProvider.selectedWorkspace,
					patient_primary_key: patientID,
				}
				const patientData = await endpointProvider.post(LINKS.DATA.PROFILING.GET_PATIENT_ATTRIBUTES, body)
				setPatientName(patientData.patient_name || "Unknown")
			} catch (error) {
				console.error('Error fetching patient data:', error)
				setPatientName("Unknown")
			}
		}

		fetchPatientData()
	}, [patientID, isClinical, endpointProvider, workspacesProvider.selectedWorkspace, LINKS.DATA.PROFILING.GET_PATIENT_ATTRIBUTES])


	// Check for header overflow
	useEffect(() => {
		const checkHeaderOverflow = () => {
			const headerContainer = document.getElementById('headerItemsContainer')
			if (headerContainer) {
				const hasOverflow = headerContainer.scrollWidth > headerContainer.clientWidth
				setHasHeaderOverflow(hasOverflow)
			}
		}

		checkHeaderOverflow()
		window.addEventListener('resize', checkHeaderOverflow)
		
		return () => {
			window.removeEventListener('resize', checkHeaderOverflow)
		}
	}, [siteName, patient_id, dataObjectName])

	function renderUnloadFile() {
		if (dataObjectID === undefined) {
			alert('dataObjectID undefined')
			return;
		}

		createModal(<ConfirmationModal
			title={"Close Data Review?"}
			description={"Are you sure you want to close Data Review?"}
			confirmButton={{
				text: "Close Data Review",
				theme: MobergTheme.BLUE,
				variant: MobergButtonVariant.FILLED,
				onClick: () => { }
			}}
			afterClose={(isCancel) => {
				if (!isCancel) {
					closeVisualization()
				}
			}}
		/>)
	}

	function renderHotkeysModal() {
		createModal(<HotkeysModal
			escClose={false}
			clickOutsideClose={false}
			updateHotkeyConfig={updateHotkeyConfig}
		/>)
	}

	function renderDisplaySettingsModal() {
		setShowZoomChecker(true)
	}

	const buildHeaderItem = (label, value) => {
		return (
			<div
				data-deidentify="true"
				style={{
					display: "flex",
					fontFamily: MobergFontFamily.HEADER,
					fontStyle: 'normal',
					fontSize: '14px',
					gap: "6px",
					whiteSpace: 'nowrap',
					flexShrink: 0
				}}
			>
				<span style={{ fontWeight: '600' }}> {label} </span>
				<span style={{ fontWeight: '400' }}> {value} </span>
			</div>
		)
	}

	return (
		<>
			<ButtonPageHeader id="buttonPageHeader">
				<ToolbarContainer>
					{/* Left Elements */}
					<LeftSection>
						<LogoSection>
							<img
							src={colorScheme === "dark" ? darkLogoIcon : logoIcon}
							alt="Logo"
							style={colorScheme === "dark"
								? { height: 32, width: "auto" }
								: { width: 42, height: 42 }
							}
						/>
						</LogoSection>

						<HeaderItemsContainer 
							id="headerItemsContainer"
							hasOverflow={hasHeaderOverflow}
						>
							<MobergRow gap="8px" expand={false}>
								{buildHeaderItem("Patient ID:", patient_id)}
								<MRNError message={mrnError} />
							</MobergRow>
							{isClinical ? buildHeaderItem("Patient name:", patientName) : buildHeaderItem("Site:", siteName)}
							{buildHeaderItem("File:", dataObjectName)}
						</HeaderItemsContainer>
					</LeftSection>

					{/* Right Elements */}
					<RightSection>
						<AlertsFeed />

						<MCPDeviceStatus />

						<MobergButton shape={MobergButtonShape.SQUARE} theme={MobergTheme.BLUE} onClick={renderHotkeysModal}>
							<MdOutlineKeyboardAlt size={MobergIconSize.LARGE} />
						</MobergButton>

						<MobergButton shape={MobergButtonShape.SQUARE} onClick={renderUnloadFile}>
							<MdIcons.MdOutlineClose size={MobergIconSize.LARGE} />
						</MobergButton>
					</RightSection>
				</ToolbarContainer>
			</ButtonPageHeader>

			<ZoomResolutionChecker 
				isVisible={showZoomChecker}
				onClose={() => setShowZoomChecker(false)}
			/>
		</>
	)
}

const ButtonPageHeader = styled.div`
	background: var(--color-surface-panel);
	color: var(--text-color);
	position: sticky;
	top: 0;
	z-index: 2;
	box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
`

const DisplayContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	border-radius: 4px;
	cursor: pointer;
	background-color: ${props => props.isOptimal ? '#eff6ff' : '#eff6ff'};
	transition: all 0.2s ease;
	border: 2px solid ${props => props.isOptimal ? '#3b82f6' : '#3b82f6'};
	
	${props => !props.isOptimal && `
		animation: borderFlash 1.5s infinite;
		
		@keyframes borderFlash {
			0%, 100% {
				border-color: #3b82f6;
			}
			50% {
				border-color: #60a5fa;
			}
		}
	`}
	
	&:hover {
		${props => !props.isOptimal && `
			transform: translateY(-1px);
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		`}
	}
`;

const DisplayIcon = styled.div`
	color: ${props => props.isOptimal ? '#000000' : '#3b82f6'};
	display: flex;
	align-items: center;
	justify-content: center;
	
	${props => !props.isOptimal && `
		animation: iconFlash 1s infinite;
		
		@keyframes iconFlash {
			0%, 100% {
				color: #3b82f6;
			}
			50% {
				color: #60a5fa;
			}
		}
	`}
`;

const DisplayText = styled.div`
	font-family: ${MobergFontFamily.HEADER};
	font-weight: 700;
	font-size: 12px;
	color: ${props => props.isOptimal ? '#000000' : '#3b82f6'};
	letter-spacing: 0.04em;
`;

const ToolbarContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 4px 8px;
	gap: 16px;
	width: 100%;
	
	@media (max-width: 768px) {
		gap: 12px;
		padding: 4px 6px;
	}
	
	@media (max-width: 480px) {
		gap: 8px;
		padding: 4px 4px;
	}
`;

const LeftSection = styled.div`
	display: flex;
	align-items: center;
	flex: 1;
	min-width: 0;
	gap: 16px;
	
	@media (max-width: 768px) {
		gap: 12px;
	}
	
	@media (max-width: 480px) {
		gap: 8px;
	}
`;

const LogoSection = styled.div`
	flex-shrink: 0;
	margin-left: 24px;
	
	@media (max-width: 768px) {
		margin-left: 16px;
	}
	
	@media (max-width: 480px) {
		margin-left: 8px;
	}
`;

const RightSection = styled.div`
	display: flex;
	align-items: center;
	flex-shrink: 0;
	gap: 8px;
`;

const HeaderItemsContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 24px;
	flex: 1;
	min-width: 0;
	overflow-x: auto;
	overflow-y: hidden;
	position: relative;
	scrollbar-width: none;
	white-space: nowrap;
	flex-wrap: nowrap;
	
	&::-webkit-scrollbar {
		display: none;
	}
	
	&::after {
		content: '';
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		width: 20px;
		background: linear-gradient(to right, transparent, var(--color-surface-panel));
		pointer-events: none;
		opacity: ${props => props.hasOverflow ? '1' : '0'};
		transition: opacity 0.2s ease;
	}
	
	@media (max-width: 768px) {
		gap: 16px;
	}
	
	@media (max-width: 480px) {
		gap: 12px;
	}
`;

export default ToolBar;
